import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Database, CheckCircle2, XCircle, CircleDashed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Empty } from "@/components/app/ui";
import { ConnectDialog } from "@/components/discovery/ConnectDialog";
import { FlowSteps } from "@/components/discovery/FlowSteps";
import { SectionHelp } from "@/components/app/Help";
import { api, can, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const ConnStatus = ({ s }) => {
  const map = { Connected: ["text-emerald-700 bg-emerald-50 border-emerald-200", CheckCircle2], Error: ["text-rose-700 bg-rose-50 border-rose-200", XCircle] };
  const [cls, Icon] = map[s.status] || ["text-slate-600 bg-slate-50 border-slate-200", CircleDashed];
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`} data-testid={`ds-status-${s.id}`}><Icon className="h-3 w-3" />{s.status}</span>;
};

const Stat = ({ label, value, tone = "text-slate-900", testId }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={testId}><div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div><div className={`mt-1 font-display text-2xl font-bold ${tone}`}>{value}</div></div>
);

export default function DataSources() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [sum, setSum] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => { api.get("/data-sources").then((r) => setRows(r.data)); api.get("/discovery/summary").then((r) => setSum(r.data)); };
  useEffect(() => { load(); }, []);
  return (
    <div>
      <PageHeader title="Data Sources & PII Discovery" testId="page-title"
        subtitle="Connect data platforms read-only, choose what to scan, and identify DPDP-relevant personal data. Only statistics are kept — never the data itself."
        actions={can(user, "datasource") && <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="connect-source-button"><Plus className="mr-1.5 h-4 w-4" />Connect Data Source</Button>} />
      <SectionHelp id="datasources" />
      <FlowSteps active={rows.length ? 0 : -1} />
      {sum && <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="Data sources" value={sum.sources} testId="sum-sources" /><Stat label="Connected" value={sum.connected} tone="text-emerald-700" testId="sum-connected" />
        <Stat label="PII columns found" value={sum.pii_columns} tone="text-blue-700" testId="sum-pii" /><Stat label="Assets with PII" value={sum.assets_with_pii} testId="sum-assets" />
        <Stat label="Critical / High" value={(sum.by_risk.Critical || 0) + (sum.by_risk.High || 0)} tone="text-rose-700" testId="sum-high" />
      </div>}
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="sources-table">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><tr>{["Source", "Platform", "Environment", "Connection", "Read-only", "Last scan", "Open PII"].map((h) => <th key={h} className="px-4 py-2.5">{h}</th>)}</tr></thead>
          <tbody>{rows.map((s) => (
            <tr key={s.id} onClick={() => nav(`/data-sources/${s.id}`)} className="cursor-pointer border-t hover:bg-slate-50" data-testid={`source-row-${s.id}`}>
              <td className="px-4 py-2.5"><div className="flex items-center gap-2 font-medium text-slate-800"><Database className="h-4 w-4 text-slate-400" />{s.name}</div>{s.last_error && <div className="mt-0.5 line-clamp-1 text-[11px] text-rose-600">{s.last_error}</div>}</td>
              <td className="px-4 py-2.5 text-slate-600">{s.type_name}</td><td className="px-4 py-2.5 text-slate-600">{s.environment}</td>
              <td className="px-4 py-2.5"><ConnStatus s={s} /></td>
              <td className="px-4 py-2.5 text-xs">{s.read_only === true ? <span className="text-emerald-700">Verified</span> : s.read_only === false ? <span className="text-rose-700">Write access!</span> : <span className="text-slate-400">Not verifiable</span>}</td>
              <td className="px-4 py-2.5 text-xs text-slate-500">{s.last_scan_status ? `${s.last_scan_status} · ${fmtDateTime(s.last_scan_at)}` : "Never"}</td>
              <td className="px-4 py-2.5 font-semibold text-blue-700">{s.open_pii}</td>
            </tr>))}</tbody>
        </table>
        {!rows.length && <Empty text="No data sources connected yet. Click “Connect Data Source” to begin." />}
      </div>
      <ConnectDialog open={open} onClose={() => setOpen(false)} onSaved={(s) => { setOpen(false); nav(`/data-sources/${s.id}`); }} />
    </div>
  );
}
